import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Autocomplete } from "@mui/material";
import { debounce } from "lodash";
import useFetchEmployeesList from "src/Pages/Company/OrganizationStructure/api/useFetchEmployeesList";
import useOrgchartStore from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Employees/store";
import __ from "src/utils/Translations";
import { useStructureStore } from "src/Pages/Company/OrganizationStructure/context/useStructureStore";
import { useCompanyId } from "src/Pages/Company/CompanyContext";
import getNoOptionsText from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/Components/Autocomplete/Utils/getNoOptionsText";
import { AxiosError } from "axios";
import { useLogStatus } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus";
import { NewRelicLogLevel } from "src/Pages/Company/OrganizationStructure/utils/NewRelicReporter/useLogStatus/useLogStatus.models";
import handleScroll from "./Utils/handleScroll";
import TextField from "./shared/TextField";
import AutocompleteOption from "./shared/AutocompleteOption";
import { TRenderOptionProps } from "./shared/AutocompleteOption/AutocompleteOption.models";

interface IOrgchartAutocompleteProps {
  name: string;
  label: string;
  required?: boolean;
  onSubmit: () => void;
}

const EmployeeAutocomplete: FC<IOrgchartAutocompleteProps> = ({
  name,
  label,
  required = false,
  onSubmit,
}) => {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const { control, resetField } = useFormContext();
  const [search, setSearch] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const structureId = useStructureStore((state) => state.structureId);
  const { setSearchedNodeId } = useOrgchartStore();
  const companyId = useCompanyId();
  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isFetching,
    isLoading,
    error: searchError,
  } = useFetchEmployeesList({
    structureId,
    companyId,
    filterString: search,
    enabled: !!companyId && !!structureId && isOpen && search.length >= 1,
  });

  const logStatus = useLogStatus();

  if (searchError) {
    logStatus({
      type: NewRelicLogLevel.ERROR,
      message: "EmployeeAutocomplete: fetchEmployeesList failed",
      statusDetails: {
        source: "API",
        operation: "fetchEmployeesList",
        status: (searchError as AxiosError)?.response?.status,
        url: (searchError as AxiosError)?.config?.url,
      },
    });
  }

  const getOptions = useCallback(() => {
    let options: {
      fk: string | null;
      initials: string | null;
      id: string | null;
      label: string | null;
      avatar: string | null | undefined;
      position: string | null;
    }[] = [];
    if (isOpen && search === "") {
      return options;
    }

    if (isOpen) {
      options =
        data?.pages.flatMap((page) =>
          page.content.map((employee) => ({
            id: employee.id,
            fk: employee.fk,
            label: employee.name,
            avatar: employee?.avatar,
            initials: employee.initials,
            position:
              employee.position && typeof employee.position === "object"
                ? employee.position.label
                : null,
          }))
        ) || [];
    }
    return options;
  }, [isOpen, search, data?.pages]);

  const debouncedSearch = useRef(
    debounce((__: SyntheticEvent, value: string) => {
      setSearch(value);
    }, 1000)
  ).current;

  const handleSearchChange = useCallback(debouncedSearch, [debouncedSearch]);

  const handleClear = () => {
    resetField(name, {
      defaultValue: "",
    });
    setIsOpen(false);
    setInputValue("");
    setSearch("");
    useOrgchartStore.getState().setSearchedNodeId(null);
    useOrgchartStore.getState().setWasSearchedNodeCleared(true);
  };

  useEffect(
    () => () => {
      debouncedSearch.cancel();
    },
    [debouncedSearch]
  );

  return (
    <Controller
      name={name}
      control={control}
      render={({
        field: { onChange, ref: fieldRef, value },
        fieldState: { invalid, error },
      }) => (
        <Autocomplete
          data-testid="os__view-search-input"
          open={isOpen}
          filterOptions={(disable) => disable}
          getOptionLabel={(option) => option.label || ""}
          options={getOptions()}
          loading={isFetching || isFetchingNextPage}
          disableListWrap
          disablePortal
          noOptionsText={__(getNoOptionsText(search.length))}
          value={value}
          getOptionKey={(option) => option.id}
          onChange={(_event, selectedValue) => {
            onChange(selectedValue);
            onSubmit();
            if (selectedValue !== null) {
              setSearchedNodeId(selectedValue.id);
              setIsOpen(false);
            } else {
              setSearchedNodeId(null);
            }
          }}
          onOpen={() => setIsOpen(true)}
          onBlur={() => setIsOpen(false)}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => {
            setInputValue(newInputValue);
            setSearch(newInputValue);
            if (
              event?.type &&
              event.type !== "click" &&
              newInputValue.length >= 1
            ) {
              setIsOpen(true);
              return handleSearchChange(event, newInputValue);
            }
            return null;
          }}
          renderInput={(params) => (
            <TextField
              params={params}
              search={search}
              inputRef={inputRef}
              isLoading={isLoading}
              isFetching={isFetching}
              isFetchingNextPage={isFetchingNextPage}
              fieldRef={fieldRef}
              handleClear={handleClear}
              invalid={invalid}
              helperText={error?.message}
              required={required}
              label={label}
            />
          )}
          renderOption={(props: TRenderOptionProps, option) => (
            <AutocompleteOption props={props} option={option} key={props.key} />
          )}
          slotProps={{
            listbox: {
              onScroll: (event) =>
                handleScroll({
                  event,
                  hasNextPage,
                  isFetchingNextPage,
                  fetchNextPage,
                }),
              style: {
                maxHeight: "300px",
                overflowY: "auto",
              },
            },
          }}
        />
      )}
    />
  );
};

export default EmployeeAutocomplete;
